import {
	faArrowRight,
	faCalendarAlt,
	faEllipsisH,
	faExclamationTriangle,
	faFilter,
	faHashtag,
	faLayerGroup,
	faPencilAlt,
	faPlay,
	faSpinner,
	faStream,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { publicApiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useToast } from "@/components/common/Toast";
import { useAuthStore } from "@/store/auth.store";
import { useEnvironmentStore } from "@/store/environments.store";
import { IRule, useRuleStore } from "@/store/rule.store";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import AddRuleForm from "./AddRuleForm";
import RuleStatusToggle from "./RuleStatusToggle";

interface RuleCondition {
	id: string;
	ruleSetId: string;
	operator: string;
	operand: string;
	value: string;
}

interface RuleAction {
	id: string;
	ruleSetId: string;
	flowId: string;
}

interface RuleSet {
	id: string;
	ruleId: string;
	order: number;
	conditions: RuleCondition[];
	actions: RuleAction[];
}

interface FlowType {
	id: string;
	name: string;
}

interface FlowAction {
	id: string;
	name: string;
}

interface RuleData {
	id: string;
	brandId: string;
	environmentId: string;
	flowActionId: string;
	flowTypeId: string;
	status: "ENABLED" | "DISABLED";
	createdAt: string;
	updatedAt: string;
	createdBy: string;
	updatedBy: string;
	flowType: FlowType;
	flowAction: FlowAction;
	ruleset: RuleSet[];
}

export const ruleApi = {
	createRule: async (
		data: IRule,
		headers: Record<string, string>
	): Promise<RuleData> => {
		const response = await publicApiClient.post(
			resolveEndpoint(API_ENDPOINTS, ["RULE", "CREATE"]),
			data,
			{ headers }
		);
		return response.data;
	},

	getRules: async (headers: Record<string, string>): Promise<RuleData[]> => {
		const response = await publicApiClient.get(
			resolveEndpoint(API_ENDPOINTS, ["RULE", "LIST"]),
			{ headers }
		);
		return response.data || [];
	},

	getRuleById: async (
		id: string,
		headers: Record<string, string>
	): Promise<RuleData> => {
		const response = await publicApiClient.get(
			resolveEndpoint(API_ENDPOINTS, ["RULE", "GET_BY_ID"], id),
			{ headers }
		);
		return response.data;
	},

	updateRule: async (
		id: string,
		data: IRule,
		headers: Record<string, string>
	): Promise<RuleData> => {
		const response = await publicApiClient.put(
			resolveEndpoint(API_ENDPOINTS, ["RULE", "UPDATE_BY_ID"], id),
			data,
			{ headers }
		);
		return response.data;
	},

	deleteRule: async (
		id: string,
		headers: Record<string, string>
	): Promise<void> => {
		const response = await publicApiClient.delete(
			resolveEndpoint(API_ENDPOINTS, ["RULE", "DELETE_BY_ID"], id),
			{ headers }
		);
		return response.data;
	},

	toggleRuleStatus: async (
		id: string,
		status: "ENABLED" | "DISABLED",
		headers: Record<string, string>
	): Promise<RuleData> => {
		const response = await publicApiClient.put(
			resolveEndpoint(API_ENDPOINTS, ["RULE", "UPDATE_STATUS_BY_ID"], id),
			{ status },
			{ headers }
		);
		return response.data;
	}
};

const useRuleQueries = (
	headers: Record<string, string>,
	brandId: string,
	selectedEnvironmentId: string,
	onDeleteSuccess?: () => void
) => {
	const queryClient = useQueryClient();
	const { success } = useToast();
	const RULE_QUERY_KEY = [`brand-rule-${brandId}-${selectedEnvironmentId}`];

	const rulesQuery = useQuery({
		queryKey: RULE_QUERY_KEY,
		queryFn: () => ruleApi.getRules(headers),
		enabled: !!brandId && !!headers.Authorization,
		staleTime: 5 * 60 * 1000 // 5 minutes
	});

	const deleteRuleMutation = useMutation({
		mutationFn: (id: string) => ruleApi.deleteRule(id, headers),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: RULE_QUERY_KEY });
			success("Rule deleted successfully");
			if (onDeleteSuccess) {
				onDeleteSuccess();
			}
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to delete rule");
		}
	});

	const toggleStatusMutation = useMutation({
		mutationFn: ({
			id,
			status
		}: {
			id: string;
			status: "ENABLED" | "DISABLED";
		}) => ruleApi.toggleRuleStatus(id, status, headers),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: RULE_QUERY_KEY });
			success("Rule status updated successfully");
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to update rule status");
		}
	});

	const createRuleMutation = useMutation({
		mutationFn: (data: IRule) => ruleApi.createRule(data, headers),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: RULE_QUERY_KEY });
			success("Rule created successfully");
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to create rule");
		}
	});

	const updateRuleMutation = useMutation({
		mutationFn: ({ id, data }: { id: string; data: IRule }) =>
			ruleApi.updateRule(id, data, headers),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: RULE_QUERY_KEY });
			success("Rule updated successfully");
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to update rule");
		}
	});

	return {
		rulesQuery,
		deleteRuleMutation,
		toggleStatusMutation,
		createRuleMutation,
		updateRuleMutation,
		RULE_QUERY_KEY
	};
};

const LoadingSpinner = () => (
	<div className="flex justify-center items-center h-screen">
		<FontAwesomeIcon icon={faSpinner} spin className="h-8 w-8 text-blue-500" />
	</div>
);

const EmptyState = () => (
	<div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center mx-4 md:mx-8 my-6">
		<div className="bg-blue-50 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4">
			<FontAwesomeIcon icon={faHashtag} />
		</div>
		<h3 className="text-xl font-semibold text-gray-900 mb-2">
			No rules created yet
		</h3>
		<p className="text-gray-600 mb-6 max-w-md mx-auto">
			Rules help you automate workflows based on specific conditions. Get
			started by creating your first business rule.
		</p>
	</div>
);

interface RuleCardProps {
	rule: RuleData;
	isMenuOpen: boolean;
	onToggleMenu: () => void;
	onEdit: () => void;
	onDelete: () => void;
	onStatusChange: () => void;
}

const RuleCard = ({
	rule,
	isMenuOpen,
	onToggleMenu,
	onEdit,
	onDelete,
	onStatusChange
}: RuleCardProps) => {
	const ruleStats = useMemo(() => {
		const ruleSets = rule.ruleset || [];
		return {
			totalRuleSets: ruleSets.length,
			totalConditions: ruleSets.reduce(
				(sum, rs) => sum + (rs.conditions?.length || 0),
				0
			),
			totalActions: ruleSets.reduce(
				(sum, rs) => sum + (rs.actions?.length || 0),
				0
			)
		};
	}, [rule.ruleset]);

	return (
		<div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 mb-4 overflow-hidden">
			<div className="flex">
				{/* Status indicator bar */}
				<div
					className={`w-2 ${
						rule.status === "ENABLED" ? "bg-green-500" : "bg-gray-300"
					}`}
				/>

				<div className="flex-1 p-6">
					<div className="flex justify-between items-center mb-4">
						<div className="flex items-center gap-3">
							<FontAwesomeIcon
								icon={faHashtag}
								className="h-6 w-6 text-blue-500 bg-blue-100 p-1 rounded-lg"
							/>
							<h3 className="font-semibold text-lg">
								Rule {rule.id.slice(-8)}
							</h3>
							<span
								className={`px-3 py-1 text-xs rounded-full font-medium ${
									rule.status === "ENABLED"
										? "bg-green-100 text-green-700"
										: "bg-red-100 text-red-700"
								}`}
							>
								{rule.status === "ENABLED" ? "Enabled" : "Disabled"}
							</span>
						</div>

						<div className="flex items-center gap-4">
							<RuleStatusToggle
								ruleId={rule.id}
								currentStatus={rule.status || "DISABLED"}
								onStatusChange={() => onStatusChange()}
							/>
							<div className="relative">
								<button
									type="button"
									onClick={onToggleMenu}
									className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
									aria-label="Toggle menu"
								>
									<FontAwesomeIcon
										icon={faEllipsisH}
										className="text-gray-500"
									/>
								</button>
								{isMenuOpen && (
									<div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
										<button
											type="button"
											className="w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-blue-50 text-left transition-colors"
											onClick={onEdit}
										>
											<FontAwesomeIcon
												icon={faPencilAlt}
												className="w-4 h-4 text-blue-600"
											/>
											Edit Rule
										</button>
										<button
											type="button"
											className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 text-left transition-colors"
											onClick={onDelete}
										>
											<FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
											Delete Rule
										</button>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="flex flex-wrap gap-4 mb-3">
						<div className="flex items-center gap-1">
							<FontAwesomeIcon
								icon={faStream}
								className="text-blue-600 w-4 h-4"
							/>
							<span className="text-gray-700">{rule.flowType.name}</span>
						</div>
						<div className="flex items-center gap-1">
							<FontAwesomeIcon
								icon={faArrowRight}
								className="text-purple-600 w-4 h-4"
							/>
							<span className="text-gray-700">{rule.flowAction.name}</span>
						</div>
					</div>

					<div className="grid grid-cols-3 gap-3 mt-4">
						<div className="bg-gray-50 rounded-lg p-3 flex flex-col items-center">
							<div className="flex items-center gap-2 mb-1">
								<FontAwesomeIcon
									icon={faLayerGroup}
									className="text-blue-500 w-4 h-4"
								/>
								<span className="font-medium text-gray-700">Rule Sets</span>
							</div>
							<span className="text-xl font-bold text-blue-600">
								{ruleStats.totalRuleSets}
							</span>
						</div>
						<div className="bg-gray-50 rounded-lg p-3 flex flex-col items-center">
							<div className="flex items-center gap-2 mb-1">
								<FontAwesomeIcon
									icon={faFilter}
									className="text-purple-500 w-4 h-4"
								/>
								<span className="font-medium text-gray-700">Conditions</span>
							</div>
							<span className="text-xl font-bold text-purple-600">
								{ruleStats.totalConditions}
							</span>
						</div>
						<div className="bg-gray-50 rounded-lg p-3 flex flex-col items-center">
							<div className="flex items-center gap-2 mb-1">
								<FontAwesomeIcon
									icon={faPlay}
									className="text-green-500 w-4 h-4"
								/>
								<span className="font-medium text-gray-700">Executions</span>
							</div>
							<span className="text-xl font-bold text-green-600">
								{ruleStats.totalActions}
							</span>
						</div>
					</div>

					<div className="flex items-center gap-4 text-xs text-gray-500 mt-4">
						<div className="flex items-center gap-1">
							<FontAwesomeIcon icon={faCalendarAlt} className="w-3 h-3" />
							<span>
								Updated {new Date(rule.updatedAt).toLocaleDateString()} at{" "}
								{new Date(rule.updatedAt).toLocaleTimeString()}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const DeleteModal = ({
	isOpen,
	ruleName,
	isDeleting,
	onConfirm,
	onCancel
}: {
	isOpen: boolean;
	ruleName: string;
	isDeleting: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}) => {
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				<div className="fixed inset-0 transition-opacity" aria-hidden="true">
					<div className="absolute inset-0 bg-gray-500 opacity-75" />
				</div>

				<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
					<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						<div className="sm:flex sm:items-start">
							<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
								<FontAwesomeIcon
									icon={faExclamationTriangle}
									className="h-6 w-6 text-red-600"
								/>
							</div>
							<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
								<h3 className="text-lg leading-6 font-medium text-gray-900">
									Delete Rule
								</h3>
								<div className="mt-2">
									<p className="text-sm text-gray-500">
										Are you sure you want to delete{" "}
										<span className="font-semibold">"{ruleName}"</span>? This
										action will delete all rule sets, conditions, and actions
										associated with this rule and cannot be undone.
									</p>
								</div>
							</div>
						</div>
					</div>
					<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
						<button
							type="button"
							className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
							onClick={onConfirm}
							disabled={isDeleting}
						>
							{isDeleting ? (
								<>
									<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
									Deleting...
								</>
							) : (
								"Delete"
							)}
						</button>
						<button
							type="button"
							className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
							onClick={onCancel}
							disabled={isDeleting}
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

const Rule = () => {
	const { brandId } = useAuthStore();
	const { selectedEnvironmentSecret, selectedEnvironmentId } =
		useEnvironmentStore();
	const {
		showCreateRule,
		setShowCreateRule,
		isDeleteModalOpen,
		ruleToDelete,
		openDeleteModal,
		closeDeleteModal,
		openEditModal,
		isEditModalOpen,
		selectedRuleId,
		closeEditModal
	} = useRuleStore();

	const [openMenuId, setOpenMenuId] = useState<string | null>(null);

	const headers = useMemo(
		() => ({
			"X-Brand-Id": brandId as string,
			Authorization: selectedEnvironmentSecret as string
		}),
		[brandId, selectedEnvironmentSecret]
	);

	const {
		rulesQuery,
		deleteRuleMutation,
		createRuleMutation,
		toggleStatusMutation,
		updateRuleMutation
	} = useRuleQueries(
		headers,
		brandId as string,
		selectedEnvironmentId as string,
		closeDeleteModal
	);

	const { data: rules = [], isLoading, error, refetch } = rulesQuery;

	const handleDeleteClick = useCallback(
		(rule: RuleData) => {
			openDeleteModal(rule.id, `Rule ${rule.id.slice(-8)}`);
			setOpenMenuId(null);
		},
		[openDeleteModal]
	);

	const handleConfirmDelete = useCallback(() => {
		if (ruleToDelete) {
			deleteRuleMutation.mutate(ruleToDelete.id, {
				onSuccess: () => {
					refetch();
					closeDeleteModal();
				}
			});
		}
	}, [ruleToDelete, deleteRuleMutation, refetch, closeDeleteModal]);

	const handleEditClick = useCallback(
		(ruleId: string) => {
			openEditModal(ruleId);
			setOpenMenuId(null);
		},
		[openEditModal]
	);

	const handleStatusChange = useCallback(
		(ruleId: string, status: "ENABLED" | "DISABLED") => {
			toggleStatusMutation.mutate(
				{
					id: ruleId,
					status: status === "ENABLED" ? "DISABLED" : "ENABLED"
				},
				{
					onSuccess: () => {
						refetch();
					}
				}
			);
		},
		[toggleStatusMutation, refetch]
	);

	const handleMenuToggle = useCallback((ruleId: string) => {
		setOpenMenuId((prev) => (prev === ruleId ? null : ruleId));
	}, []);

	const handleCreateRule = useCallback(() => {
		setShowCreateRule(true);
	}, [setShowCreateRule]);

	const handleFormSubmit = useCallback(
		(rule: IRule) => {
			if (selectedRuleId) {
				updateRuleMutation.mutate(
					{
						id: selectedRuleId,
						data: rule
					},
					{
						onSuccess: () => {
							closeEditModal();
						}
					}
				);
			} else {
				createRuleMutation.mutate(rule, {
					onSuccess: () => {
						refetch();
						setShowCreateRule(false);
					}
				});
			}
		},
		[
			selectedRuleId,
			updateRuleMutation,
			createRuleMutation,
			closeEditModal,
			setShowCreateRule,
			refetch
		]
	);

	if (isLoading) {
		return <LoadingSpinner />;
	}

	if (error) {
		return (
			<div className="flex-1 px-6 py-8">
				<div className="text-center py-12">
					<FontAwesomeIcon
						icon={faExclamationTriangle}
						size="3x"
						className="text-red-400 mx-auto mb-4"
					/>
					<h3 className="text-lg font-medium text-gray-900 mb-2">
						Failed to load rules
					</h3>
					<p className="text-gray-500">
						Please try refreshing the page or contact support if the problem
						persists.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex-1 px-6 py-8">
			<div className="flex justify-end items-center mb-6">
				<button
					type="button"
					onClick={handleCreateRule}
					className="bg-blue-fynxt text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
				>
					Create Rule
				</button>
			</div>

			<div className="bg-white border border-gray-200 rounded-lg shadow-sm">
				{rules.length === 0 ? (
					<EmptyState />
				) : (
					<div className="px-4 md:px-8 py-4">
						{rules.map((rule) => (
							<RuleCard
								key={rule.id}
								rule={rule}
								isMenuOpen={openMenuId === rule.id}
								onToggleMenu={() => handleMenuToggle(rule.id)}
								onEdit={() => handleEditClick(rule.id)}
								onDelete={() => handleDeleteClick(rule)}
								onStatusChange={() => handleStatusChange(rule.id, rule.status)}
							/>
						))}
					</div>
				)}
			</div>

			<DeleteModal
				isOpen={isDeleteModalOpen}
				ruleName={ruleToDelete?.name || ""}
				isDeleting={deleteRuleMutation.isPending}
				onConfirm={handleConfirmDelete}
				onCancel={closeDeleteModal}
			/>

			{showCreateRule && (
				<AddRuleForm
					isEditing={false}
					onClose={() => setShowCreateRule(false)}
					onSubmit={handleFormSubmit}
					authHeaders={headers}
				/>
			)}

			{isEditModalOpen && selectedRuleId && (
				<AddRuleForm
					isEditing={true}
					ruleId={selectedRuleId}
					onClose={closeEditModal}
					onSubmit={handleFormSubmit}
					authHeaders={headers}
				/>
			)}
		</div>
	);
};

export default Rule;
